import { useState } from "react";
import { Box, Button, CircularProgress, Chip, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";

import { changeLegalFileType } from "../../../services/LegalFileService";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";

const OPTIONS = [
  { value: "external", label: "COMÚN" },
  { value: "municipality", label: "MUNICIPALIDAD" },
];

function labelFor(type) {
  return type === "municipality" ? "MUNICIPALIDAD" : "COMÚN";
}

const chipSx = (type) => (theme) => {
  const isMunicipality = type === "municipality";
  const isDark = theme.palette.mode === "dark";
  return {
    fontWeight: 800,
    borderRadius: 2,
    height: 34,
    bgcolor: isMunicipality
      ? isDark
        ? "rgba(14,165,233,0.14)"
        : "rgba(14,165,233,0.10)"
      : isDark
        ? "rgba(255,255,255,0.04)"
        : "#fff",
    border: "1px solid",
    borderColor: isMunicipality ? "info.main" : isDark ? "rgba(255,255,255,0.16)" : "grey.300",
    color: isMunicipality ? "info.dark" : "text.secondary",
  };
};

export default function LegalFileTypeChip({ legalFile, onRefresh }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const canChange = hasPermission("cambiar tipo expediente");
  const current = legalFile?.type || "external";

  const [anchorEl, setAnchorEl] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!legalFile?.type) return null;

  if (!canChange) {
    return (
      <Tooltip title="Tipo de expediente">
        <Chip label={labelFor(current)} size="medium" sx={chipSx(current)} />
      </Tooltip>
    );
  }

  const handleSelect = async (value) => {
    if (value === current) {
      setAnchorEl(null);
      return;
    }
    setSaving(true);
    try {
      await changeLegalFileType(legalFile.id, value);
      showToast(`Tipo de expediente cambiado a ${labelFor(value)}.`, "success");
      setAnchorEl(null);
      await onRefresh?.();
    } catch (err) {
      showToast(err.response?.data?.message || "No se pudo cambiar el tipo.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title="Tipo de expediente — click para corregirlo">
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={saving}
          sx={(theme) => ({
            ...chipSx(current)(theme),
            px: 1.4,
            minWidth: 0,
            textTransform: "none",
            lineHeight: 1,
            "&:hover": {
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "grey.50",
            },
          })}
        >
          {saving ? <CircularProgress size={13} sx={{ mr: 0.8 }} /> : null}
          {labelFor(current)}
          <ExpandMoreRoundedIcon
            sx={{
              fontSize: 18,
              ml: 0.4,
              opacity: 0.6,
              transform: anchorEl ? "rotate(180deg)" : "none",
              transition: "transform .15s ease",
            }}
          />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { sx: { mt: 1, minWidth: 220, borderRadius: 3, py: 0.75, boxShadow: "0 16px 40px rgba(0,0,0,0.16)" } },
        }}
      >
        <Box sx={{ px: 2, py: 0.75, display: "flex", alignItems: "center", gap: 0.8 }}>
          <CategoryRoundedIcon sx={{ fontSize: 16, opacity: 0.55 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, opacity: 0.55, letterSpacing: 0.4 }}>
            TIPO DE EXPEDIENTE
          </Typography>
        </Box>
        {OPTIONS.map((opt) => {
          const selected = opt.value === current;
          return (
            <MenuItem
              key={opt.value}
              selected={selected}
              disabled={saving}
              onClick={() => handleSelect(opt.value)}
              sx={{
                py: 1,
                mx: 0.75,
                my: 0.2,
                borderRadius: 2,
                gap: 1.2,
                "&.Mui-selected": {
                  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "grey.100"),
                },
              }}
            >
              <Typography sx={{ flex: 1, fontWeight: selected ? 900 : 700, fontSize: 13.5 }}>{opt.label}</Typography>
              {selected && <CheckRoundedIcon sx={{ fontSize: 18, color: "info.main" }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
